import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CheckboxComponent } from '../checkbox/checkbox.component';

export interface TreeNode {
  id: string;
  label: string;
  checked?: boolean;
  indeterminate?: boolean;
  children?: TreeNode[];
  disabled?: boolean;
}

@Component({
  selector: 'hyperkit-checkbox-tree',
  templateUrl: './checkbox-tree.component.html',
  styleUrls: ['./checkbox-tree.component.scss'],
  standalone: true,
  imports: [CommonModule, CheckboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxTreeComponent {
  @Input() data: TreeNode[] = [];
  @Input() level = 0;
  @Output() nodeChange = new EventEmitter<TreeNode>();

  public getNodePadding(level: number): string {
    return `calc(${level} * var(--spacing-spacing-07))`;
  }

  public onNodeClick(node: TreeNode): void {
    if (node.disabled) return;
    
    const newCheckedState = !node.checked;
    
    // Create new data array with updated node
    this.data = this.data.map(n => {
      if (n.id === node.id) {
        // Update clicked node and its children
        return this.updateNodeAndChildren(n, newCheckedState);
      }
      return n;
    });
    
    // Find and emit the updated node
    const updatedNode = this.data.find(n => n.id === node.id);
    if (updatedNode) {
      this.nodeChange.emit(updatedNode);
    }
  }

  public onChildNodeChange(childNode: TreeNode, parentNode: TreeNode): void {
    // Create new data array with updated parent
    this.data = this.data.map(n => {
      if (n.id === parentNode.id) {
        // Find the updated child and create new parent with updated state
        const updatedParent = this.updateParentState({
          ...parentNode,
          children: parentNode.children?.map(child => 
            child.id === childNode.id ? childNode : child
          )
        });
        return updatedParent;
      }
      return n;
    });
    
    // Find and emit the updated parent node
    const updatedParent = this.data.find(n => n.id === parentNode.id);
    if (updatedParent) {
      this.nodeChange.emit(updatedParent);
    }
  }

  private updateNodeAndChildren(node: TreeNode, checked: boolean): TreeNode {
    // Create new node with updated state
    const updatedNode = {
      ...node,
      checked,
      indeterminate: false,
    };

    // If node has children, create new children array with updated states
    if (node.children?.length) {
      updatedNode.children = node.children.map(child => {
        if (!child.disabled) {
          return this.updateNodeAndChildren(child, checked);
        }
        return child;
      });
    }

    return updatedNode;
  }

  private updateParentState(node: TreeNode): TreeNode {
    if (!node.children?.length) return node;

    const enabledChildren = node.children.filter(child => !child.disabled);
    
    if (enabledChildren.length === 0) {
      return node;
    }

    const checkedChildren = enabledChildren.filter(child => child.checked);
    const indeterminateChildren = enabledChildren.filter(child => child.indeterminate);

    // Create new node with updated state based on children
    return {
      ...node,
      checked: checkedChildren.length === enabledChildren.length,
      indeterminate: checkedChildren.length > 0 && checkedChildren.length < enabledChildren.length || indeterminateChildren.length > 0
    };
  }
}